// Grant 2026-11-02 ("Clicking a booking from the club page just takes me to all my bookings page … There should be a
// booking detail page where I can view it directly"): the pure words behind one booking's own page
// (/your-profile/bookings/{id}), the app's booking_detail_words.dart. No DOM, no clock reads. Unit-tested.
import {areaLabelFor} from '~/utils/areaWords';
import {bookingStatusChip, dateInZone, durationWord, money, payAmount, viewerOwes, owesExtra} from '~/utils/bookingsMember';

/** The page for one booking. */
export const bookingDetailPath = (id) => `/your-profile/bookings/${id}`;

/**
 * The API's line, "Mon 20 Oct · 19:00–20:00 · Table 5", split into its day, its span and its area; the day and span
 * are written on the club's clock by the API, so the page never has to know the zone.
 */
export function lineParts(b) {
  const parts = String(b?.line ?? '').split(' · ').map((p) => p.trim());
  if (parts.length >= 3) return {day: parts[0], span: parts[1], area: parts.slice(2).join(' · ')};
  return {day: parts[0] ?? '', span: parts[1] ?? '', area: b?.playingAreaName ?? ''};
}

/** "1 h", "1 h 30", "40 min": how long the booking runs. */
export function lengthWord(b) {
  const ms = new Date(b.endsAt).getTime() - new Date(b.startsAt).getTime();
  return durationWord(Math.round(ms / 60000));
}

/** "19:00–20:00 · 1 h". */
export const whenLine = (b) => `${lineParts(b).span} · ${lengthWord(b)}`;

/** The status in a word: the list's chip, "Booked" while it is still to come, "Finished" once it is over. */
export function bookingStatusWord(b, now = new Date()) {
  if (b?.status === 'Confirmed') return new Date(b.endsAt) <= now ? 'Finished' : 'Booked';
  return bookingStatusChip(b).label;
}

/** The four statuses that hold a slot. */
const live = (b) => ['Queued', 'Held', 'PendingPayment', 'Confirmed'].includes(b?.status);

/**
 * What the viewer paid, pays or owes, to two decimals: "Free", "Paid £8.00", "Paid £8.00 · refunded £4.00",
 * "£4.00 to pay", "£3.00 to pay for the extra time", "£8.00 · waiting for payment", "Nothing charged" on a booking that
 * went before it was paid, "£8.00 · not paid yet" while a queued or held one waits.
 */
export function priceWords(b) {
  const total = b?.totalMinor ?? 0;
  const amount = payAmount(b);
  if (owesExtra(b)) return `${money(b.extraDueMinor, b.currency)} to pay for the extra time`;
  if (total <= 0 && amount <= 0) return 'Free';
  if (b?.paid || (b?.yourSharePaid && b?.status === 'PendingPayment')) {
    const paid = `Paid ${money(amount, b.currency)}`;
    return (b?.refundedMinor ?? 0) > 0 ? `${paid} · refunded ${money(b.refundedMinor, b.currency)}` : paid;
  }
  if (viewerOwes(b)) return `${money(amount, b.currency)} to pay`;
  if (b?.status === 'PendingPayment') return `${money(total, b.currency)} · waiting for payment`;
  if (!live(b)) return (b?.refundedMinor ?? 0) > 0 ? `Refunded ${money(b.refundedMinor, b.currency)}` : 'Nothing charged';
  return `${money(total, b.currency)} · not paid yet`;
}

/** The names on the booking beside the booker: ["Sam Morgan", "Ali (guest)"]. */
export const participantNames = (b) => (b?.participants ?? [])
  .map((p) => (p.name ? (p.isGuest ? `${p.name} (guest)` : p.name) : null)).filter(Boolean);

/**
 * The facts card, top to bottom: the club, the area under its own noun ("Court" / "Table", areaWords), when, the
 * status, who, the price, the cancellation line. `viewerId` turns the viewer's own name into "You".
 * Each row: {key, value, subs}.
 */
export function bookingFacts(b, {viewerId = null, now = new Date()} = {}) {
  const parts = lineParts(b);
  const facts = [];
  if (b.clubName) facts.push({key: 'Club', value: b.clubName, subs: []});
  facts.push({key: areaLabelFor(b.playingAreaName), value: b.playingAreaName || parts.area, subs: b.sportName ? [b.sportName] : []});
  facts.push({key: 'When', value: parts.day, subs: [whenLine(b)]});

  const statusSubs = [];
  if (b.status === 'Cancelled') {
    if (b.cancelledByClub) statusSubs.push('Cancelled by the club');
    if (b.cancelReason) statusSubs.push(b.cancelReason);
  } else if (b.status === 'PendingPayment') {
    if (b.shareStatusLine) statusSubs.push(b.shareStatusLine);
  }
  facts.push({key: 'Status', value: bookingStatusWord(b, now), subs: statusSubs});

  const names = participantNames(b);
  if (b.kind === 'Match') {
    facts.push({key: 'Match', value: b.title || 'From the match', subs: names.length ? [names.join(', ')] : []});
  } else {
    const self = viewerId && b.bookedByUserId && String(b.bookedByUserId).toLowerCase() === String(viewerId).toLowerCase();
    const booker = self ? 'You' : (b.bookedByName || null);
    if (booker || names.length) {
      facts.push({key: 'Who', value: booker ?? names.join(', '), subs: booker && names.length ? [`With ${names.join(', ')}`] : []});
    }
  }

  facts.push({key: 'Price', value: priceWords(b), subs: b.shareLine && !b.yourSharePaid ? [b.shareLine] : []});
  if (live(b) && b.cancelLine) facts.push({key: 'Cancelling', value: b.cancelLine, subs: []});
  return facts;
}

/** "This booking follows its match: change it from the match." under the facts when nothing can be done here. */
export function noActionLine(b, {hasAction}) {
  if (hasAction) return null;
  return b?.kind === 'Match' && live(b) ? 'This booking follows its match: change it from the match.' : null;
}

/**
 * The booking's date on the CLUB's clock, "yyyy-MM-dd" (TIME_ZONE_PLAN: always the club's clock, never the reader's):
 * the API's `day`, else its start in the API's `timeZone`; only an older API with neither falls back on the reader's
 * date. A 23:30 London booking read from New York is still London's day.
 */
export function bookDay(b) {
  if (b?.day) return String(b.day).slice(0, 10);
  if (b?.timeZone) return dateInZone(b.timeZone, new Date(b.startsAt));
  const d = new Date(b.startsAt);
  const two = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}`;
}

/** The club's Book page for the booking's own day (the API's day keys are yyyy-MM-dd on the club's clock). */
export const bookDayPath = (b) => `/clubs/${b.clubId}/book?date=${bookDay(b)}`;
